import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  decodeCliContext,
  stashCliContext,
  popCliContext,
  peekCliContext,
} from "@/helpers/cliContext";
import { AuthTracking } from "@/lib/tracking/auth";
import type { CliContext } from "@/helpers/cliContext";

/**
 * Reads the raw `cli_context` value from the URL.
 *
 * The CLI passes it in the URL *fragment* (`#cli_context=...`) so the value is
 * never transmitted to the server — it stays out of access logs and `Referer`
 * headers while remaining readable by this page. The query string is kept as a
 * fallback for older CLI builds that still append `?cli_context=...`.
 */
export function readRawCliContext(search: string, hash: string): string | null {
  const fromHash = new URLSearchParams(hash.replace(/^#/, "")).get("cli_context");
  if (fromHash) return fromHash;
  return new URLSearchParams(search).get("cli_context");
}

/**
 * Decodes and registers the CLI auth context on mount.
 *
 * Reads `cli_context` from the current URL fragment (set by the Altimate Code
 * CLI when it opens the browser auth flow; query string honoured as a
 * fallback), decodes it, and calls `AuthTracking.registerCliContext` so PostHog
 * carries the CLI session identifiers through the login / register flow. When a
 * raw `cli_context` value is present it is also stashed in sessionStorage so the
 * context survives an OAuth redirect (the fragment is dropped post-redirect).
 * Falls back to a previously stashed context if no value is present.
 *
 * @returns The resolved CliContext, or null if none was found.
 */
export function useCliContext(): CliContext | null {
  const { search, hash } = useLocation();
  // Resolve once into state so the value stays stable across re-renders. The
  // effect below pops the sessionStorage stash on mount, so re-deriving the
  // value in the render path would flip the return to null after the first
  // render (peek would find the stash already consumed).
  const [cliContext, setCliContext] = useState<CliContext | null>(
    () => decodeCliContext(readRawCliContext(search, hash)) ?? peekCliContext()
  );

  useEffect(() => {
    const rawCliContext = readRawCliContext(search, hash);
    const resolved = decodeCliContext(rawCliContext) ?? popCliContext();
    if (!resolved) return;
    AuthTracking.registerCliContext(resolved);
    if (rawCliContext) stashCliContext(resolved);
    setCliContext(resolved);
  }, [search, hash]);

  return cliContext;
}
